import React, { useEffect, useMemo, useState } from "react";
import Popup from "@/shared/components/layers/Popup/Popup";
import { UserInterface } from "@/shared/model/api.types";
import dashboardApi from "../../../api/dashboard.api";
import { DealsAnalyticsContext, DepartmentPlanDeal, MyPlanResultInterface } from "../../../model/dashboard.types";
import DealsDetailsView from "./DealsDetailsView";
import css from "./DepartmentsPlanPopup.module.css";

type DepartmentsPlanPopupProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  loading: boolean;
  deals: DepartmentPlanDeal[] | undefined;
  analytics?: DealsAnalyticsContext;
  departmentId?: number;
  departmentHeadId?: number | null;
  employees?: UserInterface[];
};

function DepartmentsPlanPopup({
  open,
  onClose,
  title,
  loading,
  deals,
  analytics,
  departmentId,
  departmentHeadId,
  employees = [],
}: DepartmentsPlanPopupProps) {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [employeeResult, setEmployeeResult] = useState<MyPlanResultInterface | null>(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState<string | null>(null);

  const sortedEmployees = useMemo(() => employees.slice().sort((a, b) => {
    if (a.id === departmentHeadId) return -1;
    if (b.id === departmentHeadId) return 1;
    return `${a.last_name} ${a.name}`.localeCompare(`${b.last_name} ${b.name}`, 'ru');
  }), [employees, departmentHeadId]);

  useEffect(() => {
    if (!open) {
      setSelectedUserId(null);
      setEmployeeResult(null);
      setEmployeeError(null);
    }
  }, [open]);

  useEffect(() => {
    let active = true;
    if (selectedUserId == null || !departmentId || !analytics) {
      setEmployeeResult(null);
      setEmployeeLoading(false);
      return () => { active = false; };
    }
    setEmployeeLoading(true);
    setEmployeeError(null);
    dashboardApi.getUserPlanResult({
      userId: selectedUserId,
      departmentId,
      dateStart: analytics.dateStart,
      dateEnd: analytics.dateEnd,
      isPeriod: true,
    }).then(response => {
      if (active) setEmployeeResult(response.data);
    }).catch(error => {
      if (active) setEmployeeError(error?.response?.data?.error_message || 'Не удалось загрузить план сотрудника');
    }).finally(() => {
      if (active) setEmployeeLoading(false);
    });
    return () => { active = false; };
  }, [selectedUserId, departmentId, analytics]);

  const selectedEmployee = sortedEmployees.find(user => user.id === selectedUserId);
  const currentDeals = selectedUserId == null ? deals : employeeResult?.deals;
  const currentAnalytics: DealsAnalyticsContext | undefined = selectedUserId == null
    ? analytics
    : analytics && employeeResult && selectedEmployee ? {
      dateStart: analytics.dateStart,
      dateEnd: analytics.dateEnd,
      scopeLabel: `${selectedEmployee.last_name} ${selectedEmployee.name}${selectedEmployee.id === departmentHeadId ? ' · руководитель' : ''}`,
      opportunityTarget: employeeResult.opportunity?.target ?? 0,
      profitTarget: employeeResult.profit?.target ?? 0,
      opportunityPreparation: employeeResult.opportunity?.valuePreparation ?? 0,
      profitPreparation: employeeResult.profit?.valuePreparation ?? 0,
      plannedSalesOpportunity: employeeResult.plannedSalesOpportunity ?? 0,
      plannedSalesProfit: employeeResult.plannedSalesProfit ?? 0,
      departmentId,
      userId: selectedUserId,
      scopeKind: 'employee',
    } : undefined;

  return (
    <Popup open={open} onClose={onClose} title={title} size="xl" fullHeight>
      {departmentId && sortedEmployees.length > 0 && <div className={css.scopePicker}>
        <div>
          <strong>Исполнитель плана</strong>
          <span>Переключайте весь отдел, руководителя или конкретного сотрудника</span>
        </div>
        <select
          value={selectedUserId ?? ''}
          onChange={event => setSelectedUserId(event.target.value ? Number(event.target.value) : null)}
        >
          <option value="">Весь отдел</option>
          {sortedEmployees.map(employee => <option key={employee.id} value={employee.id}>
            {employee.last_name} {employee.name}{employee.id === departmentHeadId ? ' — руководитель' : ''}
          </option>)}
        </select>
      </div>}
      {employeeError && <div className={css.error}>{employeeError}</div>}
      <DealsDetailsView
        loading={loading || employeeLoading}
        deals={currentDeals}
        exportTitle={selectedEmployee ? `${selectedEmployee.last_name} ${selectedEmployee.name} — сделки` : title}
        analytics={currentAnalytics}
      />
    </Popup>
  );
}

export default DepartmentsPlanPopup;
